import type {
  CustomFormAnswer,
  CustomFormSubmissionQuestion
} from "@/typings/customForm";
import { answerFieldType } from "./customFormAnswerDisplay";

export type QuestionState = "satisfied" | "unmet" | "optional";

// Mirrors the server, which is `answer_for(question)&.satisfies?(question) ||
// false` (CustomFormSubmission#question_satisfied?) — because this and the X/Y in
// the header have to agree or the view contradicts itself.
//
// The no-answer case is the one that bites: `&.` makes a missing answer row
// unsatisfied whatever the type, so a photo-only question with photo_min_count 0
// (which publish_blockers permits) must NOT be called satisfied just because
// `0 >= 0`. Only once a row exists does the photo count decide it.
export function questionSatisfied(
  question: CustomFormSubmissionQuestion,
  answer: CustomFormAnswer | null
): boolean {
  if (!answer) return false;

  const photos = answer.photos ?? [];
  const { photo_requirement, photo_min_count } = question;

  if (answerFieldType(question, answer) === "photo_only") {
    return photos.length >= photo_min_count;
  }

  return (
    answer.answered &&
    (photo_requirement !== "required_photos" ||
      photos.length >= photo_min_count)
  );
}

// The three states the rail paints and the section counts tally. Keyed on
// satisfied rather than answered: a required question that is answered but a
// photo short still gates close-out, so calling it done would contradict the
// count in the header above it.
export function questionState(
  question: CustomFormSubmissionQuestion,
  answer: CustomFormAnswer | null
): QuestionState {
  if (questionSatisfied(question, answer)) return "satisfied";
  return question.required ? "unmet" : "optional";
}
